import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  xmlContent: string
  originalXmlContent?: string
  targetLine: number   // 1-gebaseerd regelnummer
  onClose: () => void
  className?: string
  fillHeight?: boolean
}

// ── Hulpfuncties ──────────────────────────────────────────────────────────────

function getIndent(line: string): number {
  return line.length - line.trimStart().length
}

/**
 * Highlight alle voorkomens van query in text.
 * Geeft JSX terug met <mark>-elementen voor elke match.
 */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>
  const q = query.toLowerCase()
  const parts: React.ReactNode[] = []
  let lastIdx = 0

  for (let from = 0; ; ) {
    const idx = text.toLowerCase().indexOf(q, from)
    if (idx === -1) { parts.push(text.slice(lastIdx)); break }
    if (idx > lastIdx) parts.push(text.slice(lastIdx, idx))
    parts.push(
      <mark key={idx} className="bg-yellow-300 text-gray-900 rounded-sm not-italic">
        {text.slice(idx, idx + query.length)}
      </mark>
    )
    lastIdx = idx + query.length
    from = lastIdx
  }
  return <>{parts}</>
}

/**
 * Bepaal het bereik [start, end] (0-gebaseerd, inclusief) van het blok dat
 * de doelregel bevat. Werkt op basis van inspringing in opgemaakte XML.
 */
function computeBlockRange(lines: string[], targetIdx: number): [number, number] {
  const targetLine   = lines[targetIdx] ?? ''
  const targetTrimmed = targetLine.trim()
  const targetIndent  = getIndent(targetLine)

  const openOnTarget = targetTrimmed.match(/^<([^\s>/!?][^\s>/]*)(?:\s[^>]*)?>$/)
  if (openOnTarget) {
    const tagName = openOnTarget[1]
    for (let i = targetIdx + 1; i < lines.length; i++) {
      if (getIndent(lines[i]) === targetIndent && lines[i].trim() === `</${tagName}>`)
        return [targetIdx, i]
    }
    return [targetIdx, targetIdx]
  }

  let blockStart = targetIdx
  for (let i = targetIdx - 1; i >= 0; i--) {
    if (getIndent(lines[i]) < targetIndent && lines[i].trim().match(/^<[^/!?]/)) {
      blockStart = i
      break
    }
  }
  if (blockStart === targetIdx) return [targetIdx, targetIdx]

  const startIndent   = getIndent(lines[blockStart])
  const startTagMatch = lines[blockStart].trim().match(/^<([^\s>/]+)/)
  if (!startTagMatch) return [targetIdx, targetIdx]

  const startTagName = startTagMatch[1]
  let blockEnd = blockStart
  for (let i = blockStart + 1; i < lines.length; i++) {
    if (getIndent(lines[i]) === startIndent && lines[i].trim() === `</${startTagName}>`) {
      blockEnd = i
      break
    }
  }
  return [blockStart, blockEnd]
}

/**
 * Bereken alle inklapbare blokken in het XML-document.
 * Retourneert Map<openingLineIdx, closingLineIdx>.
 * Alleen elementen met tenminste één kind (j > i + 1) worden opgenomen.
 */
function computeAllBlocks(lines: string[]): Map<number, number> {
  const blocks = new Map<number, number>()
  for (let i = 0; i < lines.length; i++) {
    const trimmed  = lines[i].trim()
    const openMatch = trimmed.match(/^<([^\s>/!?][^\s>/]*)(?:\s[^>]*)?>$/)
    if (!openMatch) continue
    const tagName = openMatch[1]
    const indent  = getIndent(lines[i])
    for (let j = i + 1; j < lines.length; j++) {
      if (getIndent(lines[j]) === indent && lines[j].trim() === `</${tagName}>`) {
        if (j > i + 1) blocks.set(i, j)
        break
      }
    }
  }
  return blocks
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ValidatorXmlPanel({
  xmlContent, originalXmlContent, targetLine, onClose, className, fillHeight,
}: Props) {
  const { t } = useTranslation()
  const [rawMode, setRawMode]         = useState(false)
  const [collapsed, setCollapsed]     = useState<Set<number>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [currentMatch, setCurrentMatch] = useState(0)

  const activeContent = rawMode && originalXmlContent ? originalXmlContent : xmlContent

  const targetScrollRef = useRef<HTMLDivElement>(null)
  const matchScrollRef  = useRef<HTMLDivElement>(null)

  const lines     = useMemo(() => activeContent.split('\n'), [activeContent])
  const targetIdx = targetLine - 1

  const [blockStart, blockEnd] = useMemo(
    () => computeBlockRange(lines, targetIdx),
    [lines, targetIdx],
  )
  const allBlocks = useMemo(() => computeAllBlocks(lines), [lines])

  /** Regels die verborgen zijn omdat hun ouder is ingeklapt (leeg bij zoeken) */
  const hiddenLines = useMemo(() => {
    if (searchQuery) return new Set<number>()   // altijd uitklappen tijdens zoeken
    const hidden = new Set<number>()
    for (const [start, end] of allBlocks) {
      if (collapsed.has(start)) {
        for (let i = start + 1; i < end; i++) hidden.add(i)
      }
    }
    return hidden
  }, [allBlocks, collapsed, searchQuery])

  const visibleIndices = useMemo(
    () => lines.map((_, i) => i).filter(i => !hiddenLines.has(i)),
    [lines, hiddenLines],
  )

  const matchIndices = useMemo(() => {
    if (!searchQuery) return []
    const q = searchQuery.toLowerCase()
    return visibleIndices.filter(i => lines[i].toLowerCase().includes(q))
  }, [searchQuery, visibleIndices, lines])

  const safeMatch = matchIndices.length > 0
    ? Math.min(currentMatch, matchIndices.length - 1)
    : 0

  const toggleCollapse = useCallback((lineIdx: number) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(lineIdx)) { next.delete(lineIdx) } else { next.add(lineIdx) }
      return next
    })
  }, [])

  /**
   * Auto-expand: als het nieuwe target-regelnummer binnen een ingeklapt blok valt,
   * klap dat blok (en alle bovenliggende ingeklapte blokken) automatisch open.
   * collapsed bewust niet als dep — anders oneindige lus.
   */
  useEffect(() => {
    const toExpand: number[] = []
    for (const [start, end] of allBlocks) {
      // targetIdx > start: target is een kind (niet de opening-tag zelf)
      // targetIdx < end:   target is voor de closing-tag
      if (targetIdx > start && targetIdx < end) {
        // Lees collapsed via functionele setter om geen deps te hoeven toevoegen
        toExpand.push(start)
      }
    }
    if (toExpand.length === 0) return
    setCollapsed(prev => {
      const hasCollapsed = toExpand.some(b => prev.has(b))
      if (!hasCollapsed) return prev          // niets te doen, geen re-render
      const next = new Set(prev)
      toExpand.forEach(b => next.delete(b))
      return next
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetLine, allBlocks])

  /** true zolang targetIdx in een ingeklapt blok zit — wordt false ná auto-expand */
  const isTargetVisible = !hiddenLines.has(targetIdx)

  useEffect(() => { setCurrentMatch(0) }, [searchQuery])

  /**
   * Scroll naar target. Draait opnieuw zodra isTargetVisible true wordt
   * (d.w.z. direct ná de auto-expand re-render).
   */
  useEffect(() => {
    targetScrollRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [targetLine, isTargetVisible])

  useEffect(() => {
    if (matchIndices.length > 0)
      matchScrollRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [safeMatch, matchIndices])

  const prevMatch = () =>
    setCurrentMatch(m => (m - 1 + matchIndices.length) % matchIndices.length)
  const nextMatch = () =>
    setCurrentMatch(m => (m + 1) % matchIndices.length)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); nextMatch() }
    else if (e.key === 'Escape') setSearchQuery('')
  }

  const outerClass = className
    ?? 'border border-gray-200 rounded-xl bg-white overflow-hidden mt-4 animate-fadeSlideIn'

  return (
    <div className={`${outerClass}${fillHeight ? ' flex flex-col h-full' : ''}`}>

      {/* ── Header ── */}
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide shrink-0">
            {t('validator_xml_panel.header_line', { line: targetLine })}
          </span>

          {/* Zoekbalk */}
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('validator_xml_panel.search_placeholder')}
              className="text-xs border border-gray-200 rounded px-2 py-0.5 flex-1 min-w-0 max-w-[180px] focus:outline-none focus:border-blue-400 bg-white dark:bg-gray-700 dark:border-gray-600"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="shrink-0 text-gray-400 hover:text-gray-600 text-sm leading-none"
                title={t('validator_xml_panel.clear_search')}
              >×</button>
            )}
            {matchIndices.length > 0 && (
              <>
                <span className="shrink-0 text-xs text-gray-400 tabular-nums">
                  {safeMatch + 1}/{matchIndices.length}
                </span>
                <button onClick={prevMatch} className="shrink-0 text-xs text-gray-500 hover:text-gray-800 px-0.5" title={t('validator_xml_panel.prev_match')}>↑</button>
                <button onClick={nextMatch} className="shrink-0 text-xs text-gray-500 hover:text-gray-800 px-0.5" title={t('validator_xml_panel.next_match')}>↓</button>
              </>
            )}
            {searchQuery && matchIndices.length === 0 && (
              <span className="shrink-0 text-xs text-red-400">{t('common.no_results')}</span>
            )}
          </div>

          {originalXmlContent && (
            <button
              onClick={() => setRawMode(m => !m)}
              className={`shrink-0 text-xs px-2 py-0.5 rounded border transition-colors ${
                rawMode
                  ? 'bg-blue-100 border-blue-300 text-blue-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-400'
              }`}
              title={rawMode ? t('validator_xml_panel.show_formatted') : t('validator_xml_panel.show_original')}
            >
              {rawMode ? t('validator_xml_panel.formatted') : t('validator_xml_panel.original')}
            </button>
          )}

          <button
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-600 text-xl leading-none px-1 transition-colors"
            title={t('common.close')}
          >×</button>
        </div>
      </div>

      {/* ── Inhoud ── */}
      <div
        className={fillHeight ? 'flex-1 overflow-auto' : 'overflow-auto'}
        style={fillHeight ? undefined : { maxHeight: '420px' }}
      >
        <div className="text-xs font-mono leading-5 p-2 select-text">
          {visibleIndices.map(i => {
            const line    = lines[i]
            const lineNum = i + 1
            const isExact   = i === targetIdx
            const isInBlock = i >= blockStart && i <= blockEnd
            const isCollapsibleOpen = allBlocks.has(i) && !searchQuery
            const isCollapsedOpen   = isCollapsibleOpen && collapsed.has(i)
            const isCurrentMatch    = matchIndices[safeMatch] === i
            const isAnyMatch        = matchIndices.includes(i)

            let rowClass = 'hover:bg-gray-50'
            if (isCurrentMatch)    rowClass = 'bg-orange-100'
            else if (isAnyMatch)   rowClass = 'bg-orange-50'
            else if (isExact)      rowClass = 'bg-yellow-200'
            else if (isInBlock)    rowClass = 'bg-yellow-100'

            const theRef = isCurrentMatch ? matchScrollRef
              : (isExact ? targetScrollRef : undefined)

            return (
              <div
                key={i}
                ref={theRef as React.RefObject<HTMLDivElement>}
                className={`flex items-baseline ${rowClass}`}
              >
                {/* Regelnummer */}
                <span
                  className="select-none text-gray-300 text-right pr-3 shrink-0 tabular-nums"
                  style={{ minWidth: '3rem' }}
                >
                  {lineNum}
                </span>

                {/* Inklapknop */}
                <span className="shrink-0 w-4 text-center select-none mr-0.5">
                  {isCollapsibleOpen && (
                    <button
                      onClick={() => toggleCollapse(i)}
                      className="text-gray-400 hover:text-blue-500 leading-none"
                      title={isCollapsedOpen ? t('validator_xml_panel.expand') : t('validator_xml_panel.collapse')}
                    >
                      {isCollapsedOpen ? '▶' : '▼'}
                    </button>
                  )}
                </span>

                {/* Regelinhoud */}
                <span
                  className={`whitespace-pre ${isExact ? 'text-gray-900 font-medium' : 'text-gray-700'}`}
                >
                  <HighlightedText text={line} query={searchQuery} />
                  {isCollapsedOpen && (
                    <span className="text-gray-400 italic ml-2">
                      {t('validator_xml_panel.lines_hidden', { count: allBlocks.get(i)! - i - 1 })}
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
